import React, { CSSProperties, FC } from 'react';
import { useTageaRouter } from '@web-libs/core/routing/application/use-tagea-router.concern';
import { getCssVar } from '@web-libs/features/colors/application/colors';
import { SvgProps } from '@web-libs/ui/svgs/types';
import { useChat } from '@web-libs/features/chat/application/use-chat.context';
import { useInvitations } from '@web-libs/features/appointments/application/invitations/use-invitations';
import { useTageaConfig } from '@web-libs/core/config/config';
import DashboardIcon from '@web-libs/ui/svgs/icons/DashboardIcon';
import { routes } from '@web-app/[lang]/[tenant]/[institution]/routes-institution';
import MyAreaIcon from '@web-libs/ui/svgs/icons/MyAreaIcon';
import ChatIcon from '@web-libs/ui/svgs/icons/ChatIcon';
import CalendarIcon from '@web-libs/ui/svgs/icons/CalendarIcon';
import { FormsIcon } from '@web-libs/ui/svgs/icons/FormsIcon';
import ProfileIcon from '@web-libs/ui/svgs/icons/ProfileIcon';
import CloudIcon from '@web-libs/ui/svgs/icons/CloudIcon';
import { useUnreadCounts } from './unread-counts.context';

export type SidebarIcon = FC<SidebarIconProps>;

export interface SidebarItemDefinition {
  icon: SidebarIcon;
  active?: boolean;
  navDestination: string;
  notification?: boolean;
}

export interface SidebarIconProps {
  active?: boolean;
  width?: number;
  height?: number;
}

function asSidebarIcon(WrappedComponent: FC<SvgProps>) {
  // eslint-disable-next-line react/display-name
  return (props: SidebarIconProps) => {
    const { active, height, width } = props;
    const fill = active ? getCssVar('dark') : getCssVar('unobtrusive');
    return (
      <WrappedComponent
        fill={fill}
        {...(height && { height })}
        {...(width && { width })}
      />
    );
  };
}

export const useSidebarItems = (): SidebarItemDefinition[] => {
  const router = useTageaRouter();
  const { unreadCounts } = useUnreadCounts();
  const { isFeatureDisabled } = useTageaConfig();
  const chat = useChat();

  return [
    {
      icon: asSidebarIcon(DashboardIcon),
      navDestination: routes.dashboard,
      active: router.isCurrentPath(routes.dashboard),
    },
    {
      icon: asSidebarIcon(MyAreaIcon),
      navDestination: routes.myArea,
      active: router.isCurrentPath(routes.myArea),
      notification: unreadCounts.countInformations > 0,
    },
    {
      icon: asSidebarIcon(ChatIcon),
      navDestination: routes.chat,
      active: router.isCurrentPath(routes.chat),
      notification: chat.isUnreadMessageAvailable,
    },
    {
      icon: asSidebarIcon(CalendarIcon),
      navDestination: routes.appointments,
      active: router.isCurrentPathOrSubPath(routes.appointments),
      notification: unreadCounts.countEvents > 0,
    },
    ...(isFeatureDisabled('feature.customForms')
      ? []
      : [
          {
            icon: asSidebarIcon(FormsIcon),
            navDestination: routes.ownCustomForms,
            active: router.isCurrentPathOrSubPath(routes.ownCustomForms),
          },
        ]),
    {
      icon: asSidebarIcon(ProfileIcon),
      navDestination: routes.profile,
      active: router.isCurrentPath(routes.profile),
    },
    ...(isFeatureDisabled('feature.sharedClientInstDir')
      ? []
      : [
          {
            icon: asSidebarIcon(CloudIcon),
            navDestination: routes.fileStorage,
            active: router.isCurrentPath(routes.fileStorage),
          },
        ]),
  ];
};

interface SidebarItemProps {
  icon: SidebarIcon;
  iconSize?: number;
  navDestination: string;
  active?: boolean;
  style?: CSSProperties;
  notification?: boolean;
}

export function SidebarItem({
  icon,
  iconSize,
  active,
  navDestination,
  style,
  notification,
}: SidebarItemProps) {
  const router = useTageaRouter();
  const onClick = () => router.push(navDestination);

  const sidebarIconsSize = iconSize || 70;
  const sidebarInnerIconSize = sidebarIconsSize / 2;

  const _styles: CSSProperties = {
    width: sidebarIconsSize,
    height: sidebarIconsSize,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: notification ? 'end' : 'center',
    alignItems: 'center',
    borderRadius: 10,
    backgroundColor: active ? getCssVar('light') : getCssVar('lightShadier'),
    cursor: 'pointer',
    ...style,
  };

  return (
    <div onClick={onClick} style={_styles}>
      {icon({
        active,
        width: sidebarInnerIconSize,
        height: sidebarInnerIconSize,
      })}
      {notification && (
        <div
          style={{
            height: 5,
            width: 5,
            marginTop: 8,
            backgroundColor: getCssVar('warn'),
            borderRadius: 5,
          }}
        ></div>
      )}
    </div>
  );
}
